import { Fragment, useEffect, useRef, useState } from "react";
import type { CoworkBuildItem } from "@/components/sections/cowork-build-accordion";

/* ----------------------------------------------------------------------------
 * The AI Video page's "blank prompt to first draft" steps, laid out the way
 * Pika lays out its apps (Esther, 2026-10-28): the picture sits still on the
 * left while the step cards scroll past on the right, and it changes to match
 * whichever card is beside it.
 *
 * Same items as the build accordion (CoworkBuildItem), so the copy and media
 * stay in one place in event-detail.tsx. `icon` here is an image path: the
 * chrome-tinted marks in /images/ai-video-workshop/icons/, cut out with no tile
 * behind them. Copy is tone on tone: black headlines, grey body, the
 * highlighted words in black.
 *
 * WHICH CARD OWNS THE PICTURE is decided by geometry, not an observer: the
 * card whose centre is nearest the sticky stage's centre. Stage and cards
 * share one height, so a card is "on" exactly when it sits beside the stage.
 * An IntersectionObserver keyed to the viewport middle drifted off by half a
 * card, which put one step's picture beside the next step's words.
 *
 * Phones (< 900px) drop the stage: each card carries its own picture inline.
 * Loops follow the build accordion's rule: no bytes until needed, paused when
 * not shown, never under reduced motion (the still stays).
 * -------------------------------------------------------------------------- */

const MOBILE = "(max-width: 899px)";

function srcSet(src: string): string | undefined {
  const dot = src.lastIndexOf(".");
  if (dot < 0) return undefined;
  const stem = src.slice(0, dot);
  const ext = src.slice(dot);
  return [480, 800, 1200].map((w) => `${stem}-${w}${ext} ${w}w`).join(", ");
}

/** `[[word]]` renders in the page chrome, as the accordion's highlights do. */
function Annotated({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\[\[[^\]]+\]\])/g).map((part, i) => {
        const m = part.match(/^\[\[([^\]]+)\]\]$/);
        return m ? (
          <mark key={i} className="sbs-hl">{m[1]}</mark>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        );
      })}
    </>
  );
}

/** A still with an optional loop over it. `play` decides when the loop runs. */
function Media({ item, play }: { item: CoworkBuildItem; play: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v || !item.video) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (play) {
      if (!v.src) v.src = item.video;
      v.play().catch(() => {});
    } else if (v.src) {
      v.pause();
    }
  }, [play, item.video]);
  if (!item.image) return null;
  return (
    <>
      <img
        src={item.image}
        srcSet={srcSet(item.image)}
        sizes="(max-width: 899px) calc(100vw - 80px), 620px"
        alt=""
        loading="lazy"
        decoding="async"
      />
      {item.video && <video ref={ref} muted loop playsInline preload="none" aria-hidden="true" />}
    </>
  );
}

export function StickyBuildScroller({ items }: { items: CoworkBuildItem[] }) {
  const [active, setActive] = useState(0);
  const [mobile, setMobile] = useState(false);
  const [inline, setInline] = useState<boolean[]>(() => items.map(() => false));
  const stageRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE);
    const onMq = () => setMobile(mq.matches);
    onMq();
    mq.addEventListener("change", onMq);
    return () => mq.removeEventListener("change", onMq);
  }, []);

  // Cards take the stage's measured height (it is 3:2 of a fluid column).
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const stage = stageRef.current, root = rootRef.current;
    if (!stage || !root || !("ResizeObserver" in window)) return;
    const ro = new ResizeObserver(() => root.style.setProperty("--sbs-h", `${stage.offsetHeight}px`));
    ro.observe(stage);
    return () => ro.disconnect();
  }, []);

  // Desktop: the card beside the stage owns it.
  useEffect(() => {
    if (mobile) return;
    let raf = 0;
    const sync = () => {
      raf = 0;
      const stage = stageRef.current;
      if (!stage) return;
      const s = stage.getBoundingClientRect();
      const mid = s.top + s.height / 2;
      let best = 0;
      let bestD = Infinity;
      cardRefs.current.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const d = Math.abs(r.top + r.height / 2 - mid);
        if (d < bestD) { bestD = d; best = i; }
      });
      setActive(best);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(sync); };
    sync();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [mobile]);

  // Phones: each card's own loop runs while that card is near the viewport.
  useEffect(() => {
    if (!mobile || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        setInline((prev) => {
          const next = [...prev];
          entries.forEach((e) => { next[Number((e.target as HTMLElement).dataset.i)] = e.isIntersecting; });
          return next;
        });
      },
      { rootMargin: "200px 0px" },
    );
    cardRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [mobile]);

  return (
    <div className="sbs" ref={rootRef}>
      <style>{`
        /* #learn is overflow: hidden, which makes it a scroll container and
           stops the stage sticking. clip still clips but is not a scroll
           container, so sticky works. */
        section:has(> .sbs) { overflow: clip !important; }
        /* The section heading sits centred over the two columns. */
        /* Heading-to-content gap matches the page's other centred heading over
           a visual (Dave's section): 60px desktop, 36px phone. */
        section:has(> .sbs) div:has(> #learn-heading) { max-width: 900px !important; margin: 0 auto clamp(36px, 4.2vw, 60px) !important; text-align: center; }
        section:has(> .sbs) #learn-heading { margin-left: auto !important; margin-right: auto !important; }
        .sbs { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 20px; align-items: start; }
        .sbs-stage {
          position: sticky;
          /* clears the site nav (72px) + the crimson reserve bar (78px) */
          top: 172px;
          /* 3:2, the shape every step's picture is composed in, so none is cropped */
          aspect-ratio: 3 / 2;
          border-radius: 10px;
          overflow: hidden;
          background: #18191A;
        }
        .sbs-m { position: absolute; inset: 0; opacity: 0; transition: opacity 600ms ease; }
        .sbs-m.is-on { opacity: 1; }
        .sbs-m img, .sbs-m video, .sbs-thumb img, .sbs-thumb video {
          position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block;
        }
        .sbs-steps { display: grid; gap: 20px; }
        .sbs-card {
          /* one card = one stage height, so a card sits beside the stage exactly */
          min-height: var(--sbs-h, 420px);
          border-radius: 10px;
          background: #EFEFF3;
          padding: clamp(24px, 2.4vw, 32px);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: 28px;
          transition: background 400ms ease;
        }
        .sbs-card.is-on { background: #E9E9EE; }
        /* the chrome mark on its own, cut out, no tile */
        .sbs-ico { width: 84px; height: 84px; flex-shrink: 0; margin: -6px 0 0 -8px; }
        .sbs-ico img { width: 100%; height: 100%; display: block; }
        .sbs-card h3 {
          font-family: var(--font-display);
          font-weight: 400;
          font-size: clamp(28px, 2.6vw, 38px);
          line-height: 1.08;
          letter-spacing: -0.035em;
          color: #1D1D1F !important;
          margin: 0;
          text-wrap: balance;
        }
        /* !important: the Vivid room rules colour #learn copy for the dark
           accordion grounds, which would leave ice-blue text on grey here. */
        #learn .sbs .sbs-card p {
          margin: 14px 0 0;
          font-size: 18px;
          line-height: 1.55;
          color: rgba(29, 29, 31, 0.68) !important;
          max-width: 46ch;
          text-wrap: pretty;
        }
        /* tone on tone: grey copy, the key words in black */
        #learn .sbs .sbs-hl { background: none; color: #1D1D1F !important; font-weight: 500; }
        .sbs-thumb { display: none; }
        @media ${MOBILE} {
          .sbs { grid-template-columns: 1fr; }
          .sbs-stage { display: none; }
          .sbs-card { min-height: 0; gap: 22px; }
          .sbs-thumb {
            display: block; position: relative; aspect-ratio: 3 / 2;
            border-radius: 10px; overflow: hidden; background: #18191A;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .sbs-m, .sbs-card { transition: none; }
        }
      `}</style>

      <div className="sbs-stage" ref={stageRef} aria-hidden="true">
        {items.map((item, i) => (
          <div key={i} className={`sbs-m${i === active ? " is-on" : ""}`}>
            <Media item={item} play={!mobile && i === active} />
          </div>
        ))}
      </div>

      <div className="sbs-steps">
        {items.map((item, i) => (
          <article
            key={i}
            data-i={i}
            ref={(el) => { cardRefs.current[i] = el; }}
            className={`sbs-card${i === active && !mobile ? " is-on" : ""}`}
          >
            <div className="sbs-ico">
              <img src={item.icon} alt="" aria-hidden="true" width={84} height={84} loading="lazy" decoding="async" />
            </div>
            <div className="sbs-thumb" aria-hidden="true">
              <Media item={item} play={mobile && inline[i]} />
            </div>
            <div>
              <h3>{item.headline}</h3>
              {item.description.split("\n\n").map((para, pi) => (
                <p key={pi}><Annotated text={para} /></p>
              ))}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
